<div class="auth-container">
  <mat-card class="auth-card">
    <mat-card-header>
      <mat-card-title>Register Your Bar</mat-card-title>
      <mat-card-subtitle>Fill in all details. Your documents will be reviewed before approval.</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <mat-stepper orientation="vertical" linear #stepper>

        <!-- Step 1: Owner Details -->
        <mat-step [stepControl]="ownerForm" label="Owner Account">
          <form [formGroup]="ownerForm">
            <div class="row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>First Name</mat-label>
                <input matInput formControlName="ownerFirstName">
                <mat-error *ngIf="ownerForm.get('ownerFirstName')?.hasError('required')">Required</mat-error>
              </mat-form-field>
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Last Name</mat-label>
                <input matInput formControlName="ownerLastName">
                <mat-error *ngIf="ownerForm.get('ownerLastName')?.hasError('required')">Required</mat-error>
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email</mat-label>
              <input matInput type="email" formControlName="ownerEmail">
              <mat-error *ngIf="ownerForm.get('ownerEmail')?.hasError('required')">Required</mat-error>
              <mat-error *ngIf="ownerForm.get('ownerEmail')?.hasError('email')">Enter a valid email</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Phone</mat-label>
              <input matInput type="tel" formControlName="ownerPhone">
              <mat-error *ngIf="ownerForm.get('ownerPhone')?.hasError('required')">Required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Password</mat-label>
              <input matInput [type]="hidePassword ? 'password' : 'text'" formControlName="ownerPassword" autocomplete="new-password">
              <button mat-icon-button matSuffix type="button" (click)="hidePassword = !hidePassword">
                <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
              <mat-hint>Minimum 8 characters</mat-hint>
              <mat-error *ngIf="ownerForm.get('ownerPassword')?.hasError('required')">Required</mat-error>
              <mat-error *ngIf="ownerForm.get('ownerPassword')?.hasError('minlength')">Minimum 8 characters</mat-error>
            </mat-form-field>

            <div class="step-actions">
              <button mat-raised-button color="primary" matStepperNext [disabled]="ownerForm.invalid">Next</button>
            </div>
          </form>
        </mat-step>

        <!-- Step 2: Bar Details -->
        <mat-step [stepControl]="barForm" label="Bar Information">
          <form [formGroup]="barForm">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Bar Name</mat-label>
              <input matInput formControlName="barName">
              <mat-error *ngIf="barForm.get('barName')?.hasError('required')">Required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Description</mat-label>
              <textarea matInput formControlName="description" rows="3"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Business License Number</mat-label>
              <input matInput formControlName="licenseNumber">
              <mat-error *ngIf="barForm.get('licenseNumber')?.hasError('required')">Required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Address Line 1</mat-label>
              <input matInput formControlName="addressLine1">
              <mat-error *ngIf="barForm.get('addressLine1')?.hasError('required')">Required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Address Line 2 (optional)</mat-label>
              <input matInput formControlName="addressLine2">
            </mat-form-field>

            <div class="row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>City</mat-label>
                <input matInput formControlName="city">
                <mat-error *ngIf="barForm.get('city')?.hasError('required')">Required</mat-error>
              </mat-form-field>
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>State / Province</mat-label>
                <input matInput formControlName="state">
              </mat-form-field>
            </div>

            <div class="row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Country</mat-label>
                <input matInput formControlName="country">
                <mat-error *ngIf="barForm.get('country')?.hasError('required')">Required</mat-error>
              </mat-form-field>
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Postal Code</mat-label>
                <input matInput formControlName="postalCode">
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Bar Phone</mat-label>
              <input matInput type="tel" formControlName="phone">
              <mat-error *ngIf="barForm.get('phone')?.hasError('required')">Required</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Contact Email (optional)</mat-label>
              <input matInput type="email" formControlName="contactEmail">
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Website (optional)</mat-label>
              <input matInput formControlName="website">
            </mat-form-field>

            <div class="step-actions">
              <button mat-button matStepperPrevious>Back</button>
              <button mat-raised-button color="primary" matStepperNext [disabled]="barForm.invalid">Next</button>
            </div>
          </form>
        </mat-step>

        <!-- Step 3: Documents -->
        <mat-step label="Documents">
          <p class="section-label">Upload your identity and business documents (images or PDF).</p>

          <div class="upload-field">
            <label>ID Document — Front <span class="required">*</span></label>
            <input type="file" accept="image/*,application/pdf" (change)="onFileChange($event, 'idFront')">
            <span class="file-name" *ngIf="idFront">{{ idFront.name }}</span>
          </div>

          <div class="upload-field">
            <label>ID Document — Back <span class="required">*</span></label>
            <input type="file" accept="image/*,application/pdf" (change)="onFileChange($event, 'idBack')">
            <span class="file-name" *ngIf="idBack">{{ idBack.name }}</span>
          </div>

          <div class="upload-field">
            <label>Bar Business Registration Document <span class="required">*</span></label>
            <input type="file" accept="image/*,application/pdf" (change)="onFileChange($event, 'barDoc')">
            <span class="file-name" *ngIf="barDoc">{{ barDoc.name }}</span>
          </div>

          <div class="step-actions">
            <button mat-button matStepperPrevious>Back</button>
            <button mat-raised-button color="primary" matStepperNext [disabled]="!idFront || !idBack || !barDoc">Next</button>
          </div>
        </mat-step>

        <!-- Step 4: Payment Details -->
        <mat-step [stepControl]="paymentForm" label="Payment Details">
          <form [formGroup]="paymentForm">
            <p class="section-label">At least one payment method is required to receive payments.</p>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Bank Account Number</mat-label>
              <input matInput formControlName="bankAccountNumber">
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Mobile Money Account</mat-label>
              <input matInput formControlName="mobileMoneyAccount">
            </mat-form-field>

            <div class="step-actions">
              <button mat-button matStepperPrevious>Back</button>
              <button mat-raised-button color="accent" type="button"
                      [disabled]="loading || success || ownerForm.invalid || barForm.invalid || !idFront || !idBack || !barDoc"
                      (click)="submit()">
                <mat-spinner diameter="20" *ngIf="loading"></mat-spinner>
                <span *ngIf="!loading">Submit Registration</span>
              </button>
            </div>
          </form>
        </mat-step>

      </mat-stepper>
    </mat-card-content>

    <mat-card-actions>
      <p class="auth-link">Already have an account? <a routerLink="/login">Sign In</a></p>
    </mat-card-actions>
  </mat-card>
</div>

<style>
  .auth-container { display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; padding: 24px; }
  .auth-card { width: 100%; max-width: 640px; padding: 24px; }
  .full-width { width: 100%; margin-bottom: 16px; }
  .row { display: flex; gap: 16px; }
  .half-width { flex: 1; margin-bottom: 16px; }
  .step-actions { display: flex; gap: 12px; margin-top: 16px; justify-content: flex-end; }
  .upload-field { margin-bottom: 20px; }
  .upload-field label { display: block; font-size: 14px; font-weight: 500; margin-bottom: 8px; color: #444; }
  .upload-field input[type="file"] { display: block; }
  .file-name { font-size: 12px; color: #2e7d32; margin-top: 4px; display: block; }
  .required { color: #e53935; }
  .section-label { font-size: 14px; color: #666; margin-bottom: 16px; }
  .auth-link { text-align: center; font-size: 14px; color: #666; margin-top: 16px; }
  .auth-link a { color: #1976d2; text-decoration: none; font-weight: 500; }
  .auth-link a:hover { text-decoration: underline; }
  mat-card-title { font-size: 24px; font-weight: 700; }
</style>
